import { dateFilterRange, toISODate } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkFilter, CkTableQuery } from "@NP-Webber/crm-kit/dist/next/index.js";

/**
 * crm-kit/next DataTable query → server params (Turbo: client/src/next/tableQuery.js), handled by
 * server/src/shared/tableQuery.ts:
 *   values → vf_<col>=a,b        (bool labels כן/לא are sent as 1/0; enum labels go back to their keys via `toServer`)
 *   cond   → condop_/condval_/condval2_
 *   date   → between / gte / lte on ISO dates (presets resolved on the client; end of day inclusive)
 *   search → q · sort → sortBy/sortDir · page/pageSize → page/limit
 */
export type TableParams = Record<string, string | number>;

/** label shown in the values tab → the value the server stores (e.g. «חדש» → "new") */
export type ToServerValue = (colKey: string, label: string) => string;

export const EMPTY_TABLE_QUERY: CkTableQuery = { page: 1, pageSize: 50, search: "", sort: null, filters: {} };

export function filtersToParams(filters: Record<string, CkFilter | undefined> = {}, toServer?: ToServerValue): TableParams {
  const p: TableParams = {};
  Object.entries(filters).forEach(([k, f]) => {
    if (!f) return;
    if (f.mode === "values") {
      p[`vf_${k}`] = (f.values || [])
        .map((v) => (v === "כן" ? "1" : v === "לא" ? "0" : toServer ? toServer(k, v) : v))
        .join(",");
    } else if (f.mode === "cond") {
      p[`condop_${k}`] = f.op;
      if (f.v1 !== undefined && f.v1 !== "") p[`condval_${k}`] = f.v1;
      if (f.v2 !== undefined && f.v2 !== "") p[`condval2_${k}`] = f.v2;
    } else if (f.mode === "date") {
      const [a, b] = dateFilterRange(f);
      const from = a ? toISODate(a) : "";
      const to = b ? `${toISODate(b)} 23:59:59` : "";
      if (from && to) {
        p[`condop_${k}`] = "between";
        p[`condval_${k}`] = from;
        p[`condval2_${k}`] = to;
      } else if (from) {
        p[`condop_${k}`] = "gte";
        p[`condval_${k}`] = from;
      } else if (to) {
        p[`condop_${k}`] = "lte";
        p[`condval_${k}`] = to;
      }
    }
  });
  return p;
}

/**
 * The whole table query → params. Without a sort the server keeps its own default order
 * (unless `defaultSort` is given).
 */
export function queryToParams(
  query: CkTableQuery,
  opts: { defaultSort?: string; defaultDir?: "ASC" | "DESC"; toServer?: ToServerValue } = {},
): TableParams {
  const p: TableParams = { page: query.page || 1, limit: query.pageSize || 50 };
  const sortBy = query.sort?.key || opts.defaultSort;
  if (sortBy) {
    p.sortBy = sortBy;
    p.sortDir = query.sort ? (query.sort.dir === -1 ? "DESC" : "ASC") : opts.defaultDir ?? "DESC";
  }
  if (query.search) p.q = query.search;
  return { ...p, ...filtersToParams(query.filters, opts.toServer) };
}

/** params without paging / sorting — the audience of the current filter (for sums, column values, mailing…) */
export function filterOnly(params: TableParams): TableParams {
  const { page, limit, sortBy, sortDir, ...rest } = params; // eslint-disable-line @typescript-eslint/no-unused-vars
  return rest;
}
